import { Role } from './auth';
import type { ClinicNetworkFields } from './network';

export interface Address {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface Clinic {
  id: string;
  name: string;
  cnes?: string;
  cnpj?: string;
  address: Address;
  phone: string;
  email: string;
  logoUrl?: string;
  stampUrl?: string;
  plan: 'FREE' | 'PRO' | 'ENTERPRISE';
  aiTokenBalance: number;
  maxProfessionals: number;
  createdAt: number;
  ownerId: string;
  /** Configurações exibidas no portal do paciente. Editáveis por
   *  ADMIN e MÉDICO via Configurações > Financeiro (rule firestore
   *  permite MEDICO escrever apenas neste campo). */
  portalConfig?: {
    /** Quando true, o portal exibe o valor estimado da consulta
     *  (resolvido da tabela de preços) na tela de confirmação. */
    showProcedurePrice?: boolean;
  };
  /** Rede de Clínicas / cartão de benefícios (spec 005). Escrita SÓ via
   *  Cloud Function (credenciamento/aplicação) — as firestore rules negam
   *  qualquer mudança de `network` vinda do cliente. Ausente = fora da rede. */
  network?: ClinicNetworkFields;
}

export interface Unit {
  id: string;
  clinicId: string;
  name: string;
  address: Address;
  phone?: string;
  isActive: boolean;
  /**
   * Atendimento FORA da rede (spec 019, 17/10/2026) — ex.: "Externos
   * Rafael", o médico atendendo em vários lugares que não são da clínica.
   * Entra na agenda e na liberação como unidade (liberado lá = fora das
   * unidades da clínica), sem endereço fixo e fora do portal do paciente.
   */
  externa?: boolean;
}

export interface Professional {
  id: string;
  clinicId: string;
  unitIds: string[];
  name: string;
  email: string;
  role: Role;
  permissions: any;
  crm?: string;
  crmState?: string;
  /** CNS do profissional — usado em laudos SUS que pedem o número do
   *  Cartão Nacional do médico solicitante. */
  cns?: string;
  /** Documentos pessoais do profissional (Configurações > Meu Perfil).
   *  Fonte pra laudos/guias/termos que pedem os dados do solicitante. */
  cpf?: string;
  rg?: string;
  /** Órgão emissor do RG (ex.: "SSP/SP"). */
  rgIssuer?: string;
  /** Nascimento em epoch ms — mesma convenção de `Patient.birthDate`. */
  birthDate?: number;
  /** Registro de Qualificação de Especialista, sem o prefixo "RQE". */
  rqe?: string;
  specialty?: string;
  splitType?: 'PERCENTAGE' | 'FIXED_VALUE';
  splitValue?: number;
  photoUrl?: string;
  /**
   * Agenda específica por unidade (override da config da clínica).
   * Estrutura: `{ [unitId]: { days: { '0': {enabled,start,end}, ..., '6': {...} } } }`.
   * Sem entrada pra uma unidade, o backend cai no `agendaConfig.days` geral
   * da clínica. Permite "Dr. X só atende seg/qua na Sede; ter/qui na Filial".
   * Pré-condição: o `unitId` precisa estar em `unitIds` — se não tiver,
   * o profissional nem aparece pra essa unidade no portal.
   */
  unitSchedules?: Record<
    string,
    {
      days: Record<string, { enabled: boolean; start: string; end: string }>;
    }
  >;
  /**
   * Exceções pontuais por data. Numa data com override, o profissional
   * atende SOMENTE na `unitId` especificada (mesmo que normalmente
   * atendesse em outras), com os horários `start`/`end` do override —
   * ou herda os do `unitSchedules[unitId]` da semana, se omitidos.
   *
   * Casos de uso:
   *   - "Dia 10/04 vou atender na Filial Centro" (deslocamento pontual)
   *   - "Sábado de plantão das 8h ao meio-dia" (atendimento extra)
   *
   * Pré-condição: `unitId` do override deve estar em `unitIds` (caso
   * contrário a UI não persiste). Pra cancelar atendimento numa data
   * específica do prof, use `blockedDates` clínica-wide ou marque
   * todos os dias como disabled no schedule semanal.
   */
  unitDateOverrides?: Array<{
    date: string;     // 'YYYY-MM-DD'
    unitId: string;
    start?: string;   // 'HH:MM' opcional — sem isso herda unitSchedules
    end?: string;
    notes?: string;
  }>;
  /**
   * Regras de agenda próprias deste médico (spec 010, fase 3) — merge por
   * cima da `AgendaConfig` da clínica. Campo ausente, ou chave ausente
   * dentro dele, significa "herda a clínica": quem não configurou nada
   * não muda de comportamento.
   *
   * O que NÃO entra aqui de propósito:
   *   - `slotIntervalMin` — a grade de horários fixos é UMA grade visual
   *     na tela; dois médicos com intervalos diferentes deixariam a
   *     coluna do dia incoerente. Intervalo continua sendo da clínica.
   *   - `fixedSlots` — mesma razão.
   */
  agendaRules?: {
    /** Duração padrão da consulta DELE, em minutos. */
    defaultDuration?: number;
    /** Teto de encaixes por horário DELE. */
    overbookingLimit?: number;
    /**
     * Tipos de atendimento que ele faz ("Dr. B não opera"). Ausente ou
     * lista vazia = faz todos — nunca trancar o médico fora da própria
     * agenda por causa de um checkbox desmarcado sem querer.
     */
    allowedTypes?: Appointment['type'][];
  };
  /**
   * Escopo de agenda deste perfil: **de quais médicos ele pode ver e
   * alterar agendamentos**. Ausente ou vazio = todos (comportamento de
   * sempre, e o que vale pra todo mundo que já existe).
   *
   * Existe pro caso de recepção compartilhada entre unidades/médicos:
   * a recepção que atende o Dr. Rafael não mexe na agenda do Dr.
   * Leopoldo, mesmo estando na mesma clínica.
   *
   * **É regra de acesso, não preferência de tela.** A trava de escrita
   * mora nas firestore rules (`canTouchAgendaOf`), que leem exatamente
   * este campo — o filtro no app é a cara dela, não a fechadura.
   * ADMIN e o dono da clínica passam por cima (precisam da visão toda).
   */
  allowedProfessionalIds?: string[];
  allowedHours?: Record<string, { start: string, end: string }>;
  allowedIPs?: string[];
  hasCompletedTour: boolean;
  tourVersion?: string;
  isActive: boolean;
  createdAt: number;
}

export interface Patient {
  id: string;
  clinicId: string;
  name: string;
  /** Nome social — usado preferencialmente na UI quando preenchido (pacientes
   *  trans, apelidos que dão a identidade legal). O `name` continua sendo o
   *  nome civil pra laudos/convênio. */
  socialName?: string;
  cpf?: string;
  cpfHash?: string;
  /** Primeiros dígitos do CPF em claro, para busca por prefixo. */
  cpfPrefix?: string;
  cpfOwnerType?: 'PROPRIO' | 'PARENTE' | 'OUTRO';
  cpfOwnerName?: string;
  rg?: string;
  /** Outros documentos de identidade além de RG/CPF (passaporte, CNH, CTPS,
   *  certidão de nascimento, etc.). Texto livre no campo number. */
  otherDocuments?: PatientOtherDocument[];
  /** Cartão Nacional de Saúde — usado em laudos SUS. Opcional porque
   *  a maior parte dos pacientes não tem isso à mão no cadastro. */
  cns?: string;
  /** Cartões CNS adicionais (alguns pacientes têm mais de um — antigo +
   *  emitido em outro estado). Array de strings só com o número. */
  additionalCnsCards?: string[];
  patientNumber: string;
  phone: string;
  /**
   * Telefone canônico derivado de `phone`: SÓ dígitos, com DDD (10 ou 11).
   * Ausente quando o campo livre não rende um número utilizável — o que
   * inclui os ~27% da base migrada do HiDoctor que vieram sem DDD.
   *
   * Existe porque `phone` é texto livre da recepção (máscara, dois números
   * no mesmo campo, anotações como "99999999 Gerlania"), e comparar isso
   * com o que o paciente digita no portal nunca casava. Derivado
   * automaticamente em createPatient/updatePatient — NÃO escreva à mão.
   * Ver src/utils/phone.ts.
   */
  phoneDigits?: string | null;
  /**
   * Origem da ficha. `'PORTAL_SELF'` = o próprio paciente se cadastrou pelo
   * portal, sem ninguém conferir documento. Ausente = criada internamente
   * (recepção, totem, agendador) ou migrada do sistema antigo.
   */
  source?: 'PORTAL_SELF';
  /** false em ficha de auto-cadastro: nenhum documento foi conferido. */
  identityVerified?: boolean;
  /** true entre o auto-cadastro pelo portal e o "Conferido" da secretaria
   *  na Caixa de Pendências. NÃO derive isto de `registrationStatus`: a
   *  base migrada é quase toda MINIMAL, e a fila nunca esvaziaria. */
  portalReviewPending?: boolean;
  /** Epoch ms do auto-cadastro. Existe porque `createdAt` é serverTimestamp
   *  (Timestamp do Firestore) e as telas que ordenam/formatam esperam number. */
  portalSignupAt?: number;
  phoneContactName?: string;
  /** Telefones secundários — fixo de casa, recado, trabalho. `phone` segue
   *  sendo o celular/WhatsApp principal. */
  phoneAlt1?: string;
  phoneAlt2?: string;
  email?: string;
  birthDate?: number;
  gender?: 'M' | 'F' | 'OTHER';

  // ── Filiação e dados sociodemográficos ─────────────────────────────
  // Campos pedidos em laudos, guias de convênio e relatórios SUS. Texto
  // livre pra dar flexibilidade — selects são só na UI.
  /** Mês+dia de nascimento normalizado como "MM-DD" — denormalização pra
   *  permitir filtro de aniversariantes do mês/dia via range query no
   *  Firestore. Populado em create/update a partir de `birthDate`. */
  birthDayMonth?: string;
  ethnicity?: string;
  race?: string;
  naturality?: string;
  nationality?: string;
  profession?: string;
  maritalStatus?: string;
  motherName?: string;
  motherProfession?: string;
  fatherName?: string;
  fatherProfession?: string;
  spouseName?: string;
  /** ID do paciente cadastrado vinculado como mãe. Quando preenchido,
   *  a aba Cadastro oferece atalho pra abrir a ficha e a árvore
   *  genealógica é mantida em sincronia (cria `FamilyMember` espelhado).
   *  `motherName` continua sendo o nome literal pra laudos/convênio. */
  motherId?: string;
  /** ID do paciente cadastrado vinculado como pai. */
  fatherId?: string;
  /** ID do paciente cadastrado vinculado como cônjuge. */
  spouseId?: string;

  /** Recém-nascido na guia do convênio — usado em maternidade/pediatria
   *  pra indicar que a guia deve ser emitida em nome da mãe. */
  isRnConvenio?: boolean;
  /** Código do paciente em outro sistema (importação/migração). Apenas
   *  texto livre — não interfere no `patientNumber` gerado pelo sistema. */
  legacyCode?: string;
  /** Observações gerais do cadastro (campo livre). Não confundir com
   *  notas clínicas — essas vivem em `ClinicalRecord`. */
  observations?: string;
  /**
   * Anotação de ALERTA sobre o paciente (campo livre) — o que quem atende
   * precisa saber ANTES de começar: "surdo, fale de frente", "cadeirante",
   * "acompanhado sempre pela filha", "não fala do diagnóstico na frente do
   * marido". Diferente de `observations`, isto ABRE UM POP-UP na ficha (ver
   * `PatientAlertModal`), junto com o nome social.
   */
  alertNote?: string;
  /**
   * Quem vê o `alertNote` no pop-up — escolhido na hora de gravar o alerta:
   *  - 'ALL'      → toda a equipe (recepção inclusive);
   *  - 'CLINICAL' → só quem tem acesso ao prontuário (padrão, mais restrito).
   * Não afeta o nome social, que aparece pra todos.
   */
  alertVisibility?: 'ALL' | 'CLINICAL';
  /** "Como nos conheceu?" — lead source pra marketing/relatórios. */
  leadSource?: string;

  // ── Antropometria / dados clínicos básicos ─────────────────────────
  /** Tipo sanguíneo (A+, O-, etc. ou DESCONHECIDO). Texto livre — UI
   *  oferece select com as opções padrão. */
  bloodType?: string;
  /** Peso em quilogramas. */
  weight?: number;
  /** Altura em metros (ex: 1.78). */
  height?: number;

  address?: Address;
  photoUrl?: string;
  guardians?: Guardian[];
  insurers?: PatientInsurer[];
  tags?: string[];
  idDocuments?: PatientIdDocument[];

  // ── Resumo clínico (Fase 2 do redesign) ──
  // Campos opcionais editados pelo modal "Editar resumo clínico" do
  // sidebar da ficha. Não interferem em outros fluxos — quando vazios
  // o sidebar simplesmente não renderiza a seção.
  allergies?: string[];
  activeDiagnoses?: PatientDiagnosis[];
  currentMedications?: PatientMedication[];

  registrationStatus: 'MINIMAL' | 'COMPLETE';
  lgpdConsentDate?: number;
  lgpdConsentVersion?: string;
  lgpdDeletionRequested?: boolean;
  /**
   * Data sugerida para retorno ao consultório (epoch ms, meia-noite local).
   * Marcada pelo médico no modal de Conduta. O portal do paciente exibe um
   * alerta a partir dessa data. `null` quando o médico não definiu retorno.
   */
  recommendedReturnAt?: number | null;
  /** Quando essa recomendação foi gravada (epoch ms) — usado pra o paciente saber em qual consulta foi pedido. */
  recommendedReturnSetAt?: number | null;
  /**
   * Prazo recomendado para realização da cirurgia/procedimento solicitado
   * (epoch ms, meia-noite local do dia 1 do mês). Marcado no modal de
   * Solicitação de Cirurgias. O portal do paciente exibe alerta separado
   * do retorno de consulta. `null` quando o médico não definiu prazo.
   */
  recommendedSurgeryAt?: number | null;
  /** Quando o prazo de cirurgia foi gravado (epoch ms). */
  recommendedSurgerySetAt?: number | null;
  isDeleted: boolean;
  deletedAt?: number;
  deletedBy?: string;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
}

/**
 * Patient identification document (RG, CNH, CPF, etc.) uploaded during registration
 * or later. Photo is compressed and stored in Firebase Storage; the URL is kept here.
 */
export interface PatientIdDocument {
  id: string;
  type: 'RG' | 'CNH' | 'CPF' | 'OUTRO';
  url: string;
  fileName: string;
  uploadedAt: number;
  ocrExtracted?: boolean;
}

export interface Guardian {
  name: string;
  cpf?: string;
  relationship: 'PAI' | 'MAE' | 'RESPONSAVEL';
  phone?: string;
}

export interface PatientInsurer {
  insurerId: string;
  cardNumber: string;
  plan?: string;
  validUntil?: number;
  /** Quando true, a carteira não tem data de validade (vitalícia / sem
   *  vencimento). Quando true, `validUntil` é ignorado. */
  validUntilIndefinite?: boolean;
}

/**
 * Documento de identidade alternativo (passaporte, CNH, CTPS, certidão,
 * etc.) — guardamos apenas tipo + número. Foto/upload continua passando
 * por `PatientIdDocument`.
 */
export interface PatientOtherDocument {
  type: 'PASSAPORTE' | 'CNH' | 'CTPS' | 'CERTIDAO_NASCIMENTO' | 'TITULO_ELEITOR' | 'OUTRO';
  number: string;
}

/**
 * Diagnóstico ativo do paciente (Fase 2 — sidebar clínico).
 * `code` é opcional pra permitir cadastrar sem CID; quando preenchido
 * deve seguir CID-10 (ex: "H40.11").
 */
export interface PatientDiagnosis {
  code?: string;
  label: string;
}

/**
 * Medicação em uso contínuo (Fase 2 — sidebar clínico).
 * `posology` é texto livre — médico escreve algo como
 * "1 gota OE · 22h · contínuo" sem precisar estruturar.
 */
export interface PatientMedication {
  name: string;
  posology?: string;
}

/**
 * Parentesco do membro familiar em relação ao paciente. A enumeração é
 * usada pra posicionar o nó no diagrama da árvore genealógica: cada
 * valor mapeia pra uma geração (-2 avós … +2 netos) e um lado
 * (paterno/materno) — ver `src/utils/familyConstants.ts`.
 */
export type FamilyRelationship =
  | 'AVO_PATERNO' | 'AVO_PATERNA' | 'AVO_MATERNO' | 'AVO_MATERNA'
  | 'PAI' | 'MAE'
  | 'TIO_PATERNO' | 'TIA_PATERNA' | 'TIO_MATERNO' | 'TIA_MATERNA'
  | 'CONJUGE'
  | 'IRMAO' | 'IRMA' | 'MEIO_IRMAO' | 'MEIO_IRMA'
  | 'PRIMO' | 'PRIMA'
  | 'FILHO' | 'FILHA'
  | 'SOBRINHO' | 'SOBRINHA'
  | 'NETO' | 'NETA'
  | 'OUTRO';

/**
 * Parente cadastrado pra histórico familiar de doenças. Vive em
 * `clinics/{cid}/patients/{pid}/familyMembers`. `conditions` reusa
 * `PatientDiagnosis` pra manter o mesmo formato do resumo clínico
 * (label + CID opcional), facilitando filtro por doença.
 */
export interface FamilyMember {
  id: string;
  patientId: string;
  name: string;
  relationship: FamilyRelationship;
  /** Texto livre quando `relationship === 'OUTRO'` (ex: "padrasto",
   *  "madrinha"). Ignorado pros demais valores. */
  relationshipLabel?: string;
  birthDate?: number;
  deceased?: boolean;
  /** Causa do falecimento (texto livre) — relevante pra histórico. */
  deceasedCauseLabel?: string;
  conditions?: PatientDiagnosis[];
  notes?: string;
  /** Quando o parente também é um paciente cadastrado da clínica.
   *  Permite abrir a ficha completa dele a partir do registro familiar
   *  e foi preenchido via busca no modal "Adicionar parente". */
  linkedPatientId?: string;
  /** Quando preenchido, este doc é um REFLEXO automático criado a partir
   *  de um cadastro de parente em outra ficha. Read-only — pra editar
   *  ou excluir, abra a ficha do paciente origem (`sourcePatientId`).
   *  Garante simetria: se Filho cadastrou Mãe como parente vinculado, a
   *  ficha da Mãe ganha o Filho automaticamente. */
  mirrorOf?: {
    sourcePatientId: string;
    sourceMemberId: string;
    /** Nome do paciente origem denormalizado pro badge no card. */
    sourcePatientName: string;
    /** Quando true, o lado paterno/materno do parentesco foi escolhido
     *  por convenção porque não pôde ser derivado (caso de NETO→AVO,
     *  SOBRINHO→TIO). UI exibe aviso "lado estimado". */
    lateralityEstimated?: boolean;
  };
  /** Quando preenchido, este doc tem um reflexo em
   *  `clinics/{cid}/patients/{linkedPatientId}/familyMembers/{mirrorMemberId}`.
   *  Usado pra propagar delete em cascata. */
  mirrorMemberId?: string;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
}

/**
 * Item faturável de um atendimento (Cobrança — fluxo multi-procedimento).
 *
 * Clínica oftalmológica costuma fazer vários exames/procedimentos na
 * mesma visita (ex.: Consulta + Topografia + Paquimetria, ou bateria
 * pré-op de catarata: Biometria + Microscopia + Topografia). Cada um
 * tem valor próprio. Em vez de poluir a agenda com um card por exame
 * (como no HiDoctor), o dratento mantém UM card por visita e lança os
 * itens aqui dentro.
 *
 * `Appointment.chargedAmount` permanece a fonte de verdade pros totais
 * (rodapé do dia, balanço, dashboard, relatórios, TISS) — é mantido em
 * sincronia como a SOMA de `procedures[].price` ao salvar a cobrança.
 * `procedures[]` é só o detalhamento pra exibição/impressão/auditoria.
 */
export interface AppointmentProcedure {
  /** Nome do procedimento/exame (snapshot — "Topografia", "Paquimetria",
   *  "Capsulotomia"). Denormalizado pra permanecer legível mesmo que a
   *  tabela de preços mude/desative depois. */
  name: string;
  /** Valor cobrado deste item em reais. */
  price: number;
  /** Categoria base pra relatórios (herda da tabela de preços de origem
   *  quando lançado a partir dela). Default 'PROCEDIMENTO'. */
  type?: 'CONSULTA' | 'RETORNO' | 'PRIMEIRA_VEZ' | 'EXAME' | 'TELEMEDICINA' | 'PROCEDIMENTO';
  /** Quando `type='EXAME'`, qual exame (OCT, PAQUIMETRIA…) — espelha o
   *  `examType` da tabela de preços pra rastrear a origem. */
  examType?: string;
  /** Id do registro em `procedurePrices` que originou o item (quando
   *  veio da tabela). Texto livre quando digitado à mão = ausente. */
  priceId?: string;
  /** Quando o item veio de uma tabela de preços de modelo de orçamento
   *  (ex.: "Tabela de preços NOANE 2026", de OUTRA clínica) em vez de
   *  `procedurePrices`: id e nome da tabela. Nunca junto com `priceId`. */
  priceTableId?: string;
  priceTableName?: string;
  /** Tempo de realização deste item, em minutos — snapshot do que estava
   *  configurado em Configurações › Checklist (exames/procedimentos) na
   *  hora de agendar. Permite à agenda posicionar cada item no seu
   *  horário sem reler a config. Ausente = duração padrão da agenda. */
  minutes?: number;
}

/**
 * Adiantamento registrado num atendimento — sinal/entrada de cirurgia ou
 * parcela paga antes da quitação. A soma de `payments[].amount` alimenta
 * `Appointment.amountPaid`; o saldo a receber é `chargedAmount - amountPaid`.
 */
export interface AppointmentPayment {
  /** Valor recebido nesta entrada (R$). */
  amount: number;
  /** Quando foi recebido (ms epoch). */
  at: number;
  /** Forma de pagamento desta entrada (Pix, Dinheiro, Cartão…). */
  method?: string;
  /** UID de quem registrou — auditoria. */
  by?: string;
  /** Nome (snapshot) de quem registrou. */
  byName?: string;
  /** Observação opcional ("sinal", "1ª parcela", "quitação"). */
  note?: string;
}

/**
 * Forma de pagamento que compõe a cobrança de um atendimento.
 *
 * Uma cobrança pode ser quitada por UMA forma (1 item) ou por VÁRIAS
 * (pagamento híbrido — ex.: cartão de crédito 6× R$4.000 + Pix R$1.000).
 * A soma de `paymentParts[].amount` corresponde ao valor cobrado (ou ao
 * recebido, no caso de parcial). Alimenta o relatório de recebimentos
 * por forma de pagamento (Pix, espécie, cartão com nº de parcelas,
 * híbrido, transferência…).
 */
export interface PaymentPart {
  /** Forma de pagamento desta parte. */
  method:
    | 'PIX'
    | 'DINHEIRO'
    | 'CARTAO_CREDITO'
    | 'CARTAO_DEBITO'
    | 'TRANSFERENCIA'
    | 'ANTECIPADO'
    | 'CONVENIO'
    | 'PACOTE'
    | 'OUTRO';
  /** Valor pago nesta forma (R$). */
  amount: number;
  /** Nº de parcelas — relevante só pra cartão de crédito. Ausente ou 1 =
   *  à vista. Permite o detalhamento "em quantas parcelas" no relatório. */
  installments?: number;
  /** LEGADO (21/09/2026, poucas horas no ar): a forma 'ANTECIPADO' com
   *  a observação de onde estava registrado. Hoje o momento vive em
   *  `Appointment.paymentTiming` + `paymentNote`; a forma é só o "como". */
  note?: string;
}

export interface Appointment {
  id: string;
  clinicId: string;
  unitId: string;
  patientId: string;
  patientName?: string;
  professionalId?: string;
  equipmentId?: string;
  type: 'CONSULTA' | 'RETORNO' | 'PRIMEIRA_VEZ' | 'EXAME' | 'TELEMEDICINA' | 'PROCEDIMENTO';
  /** Referência opcional pro cadastro custom de tipos (`clinics/{c}/appointmentTypes`).
   *  Quando preenchida, UI usa nome/cor do custom no card; o `type` enum
   *  acima permanece como categoria base pra preços/cotas/relatórios. */
  appointmentTypeId?: string;
  /** Prioridade clínica da consulta — 'URGENCIA' fura a fila de "próximo
   *  paciente" (ver `pickNextUp`) e sobe na gaveta lateral pra logo após quem
   *  está em atendimento. Ausente = eletiva (comportamento legado). */
  priority?: 'ELETIVA' | 'URGENCIA';
  /**
   * Lateralidade do procedimento/exame marcado (OD / OE / AO). Escolhida no
   * agendamento e mostrada na linha da agenda; os Documentos em lote a
   * herdam quando os pacientes vêm da agenda. Ausente = não informada
   * (consultas, ou agendamentos anteriores ao campo).
   */
  eye?: 'OD' | 'OE' | 'AO';
  status:
    | 'PENDENTE'        // aguardando aprovação do staff (criada via portal/agendador com flag de aprovação ligada)
    | 'AGENDADA'
    | 'CONFIRMADA'
    | 'EM_ESPERA'
    | 'EM_ATENDIMENTO'
    | 'CONCLUIDA'
    | 'CANCELADA'
    | 'FALTOU';
  date: number;
  startTime: string;
  endTime: string;
  insurerId?: string;
  /**
   * Marcador POSITIVO de como a cobrança do dia foi escolhida, pra o selo
   * "convênio de hoje" refletir com certeza (e alertar quando faltar):
   *  - 'CONVENIO'   → convênio escolhido (acompanha `insurerId`);
   *  - 'PARTICULAR' → particular escolhido DE PROPÓSITO (sem `insurerId`);
   *  - ausente      → NÃO selecionado (legado/portal) → dispara o alerta.
   * Agendamentos novos criados pelo formulário sempre gravam este campo
   * (a escolha é obrigatória). `insurerId` presente já é convênio inequívoco
   * mesmo em registros antigos sem este marcador.
   */
  insurerChoice?: 'CONVENIO' | 'PARTICULAR';
  /**
   * Acompanhante do dia informado no agendamento — vínculo a um paciente
   * cadastrado (id + nome + CPF em claro, snapshot). Flui pra sessão do dia
   * (declaração de acompanhante) quando a ficha do paciente abre.
   */
  accompanistId?: string;
  accompanistName?: string;
  accompanistCpf?: string;
  /**
   * true depois que a recepção respondeu à pergunta "vai entrar acompanhado?"
   * — MESMO quando a resposta é "entra sozinho" (que não deixa accompanistId).
   * Distingue "decidiu que vai só" de "nunca foi perguntado"; o botão
   * "Avisar médico" só re-pergunta enquanto isto não existe.
   */
  accompanistAsked?: boolean;
  /**
   * Origem do agendamento. Gravado pelo backend no momento da criação:
   *  - 'PORTAL' → o próprio paciente marcou pelo portal (POST /portal/book).
   *    É o que a cota diária de agendamento online conta
   *    (`maxOnlineBookingsPerDay` em `clinics/{cid}/config/agenda`).
   *  - ausente  → criado internamente pelo staff, ou registro legado
   *    anterior a este campo.
   * NUNCA reescreva este campo em update: a contagem de vagas online
   * depende dele, e mudá-lo libera ou consome vaga retroativamente.
   */
  source?: 'PORTAL';
  /** Epoch ms do envio da confirmação por WhatsApp (agendamento do portal
   *  que virou AGENDADA). Presente = já enviada; é o que impede reenvio e
   *  corta a recursão do trigger, que reescreve o próprio doc.
   *  Ver functions/src/triggers/appointmentConfirmation.ts. */
  confirmationSentAt?: number;
  confirmationMessageId?: string;
  /** true entre o cancelamento feito pelo paciente no portal e o "Ciente"
   *  da secretaria na Caixa de Pendências. Existe porque um cancelamento
   *  pelo portal era invisível: a consulta sumia da agenda e ninguém
   *  sabia que a vaga tinha vagado. */
  portalCancelPending?: boolean;
  isOverbooking: boolean;
  overbookingLimit?: number;
  /**
   * Agendado "sem hora" (opção da grade de horários fixos): o paciente entra
   * na SEQUÊNCIA, após o último agendamento do dia. `startTime`/`endTime`
   * recebem horários sintéticos (logo após o último paciente) só pra
   * ordenação/posicionamento — a UI exibe o prefixo "A seguir" no lugar
   * da hora.
   */
  noTime?: boolean;
  /**
   * "Fora das vagas" (21/10/2026): retorno de cirurgia / procedimento (YAG…)
   * marcado pela equipe sem ocupar vaga — o portal, a cota, os consultórios
   * e o teto de retornos o ignoram, e não há limite por dia. Ver
   * `src/utils/extraSlot.ts`. Ausente = agendamento normal.
   */
  extraSlot?: boolean;
  examType?: string;
  prepInstructions?: string;
  returnDays?: number;
  notes?: string;
  cancellationReason?: string;
  reminderSent: boolean;
  /** Epoch ms do envio do lembrete D-1 (cron WhatsApp Cloud API). */
  reminderSentAt?: number;
  reminderResponse?: 'CONFIRMED' | 'CANCELLED' | 'NO_RESPONSE';
  reminderMessageId?: string;
  createdAt: number;
  createdBy: string;

  // ── Cobrança (Fase 2 — Cobranças) ────────────────────────────────
  // Todos opcionais pra preservar agendamentos antigos. O staff
  // preenche pela section "Cobrança" no AppointmentDetailModal; o
  // valor sugerido vem da tabela de preços (`procedurePrices`)
  // resolvida em runtime baseada em `type + examType + insurerId`.

  /** Valor sugerido pela tabela de preços no momento que o agendamento
   *  foi aberto. Snapshot — não muda mesmo se a tabela de preços for
   *  alterada depois. Permite auditar "quanto era o preço base na
   *  época" mesmo que `chargedAmount` tenha divergido por desconto. */
  expectedPrice?: number;
  /** Valor efetivamente cobrado do paciente. Pode divergir do
   *  `expectedPrice` (desconto, pacote, isenção parcial). Quando
   *  `procedures` está preenchido, este campo é a SOMA dos itens
   *  (mantido em sincronia ao salvar) — continua sendo a fonte de
   *  verdade pros totais existentes. */
  chargedAmount?: number;
  /** Detalhamento dos itens faturáveis do atendimento (Consulta +
   *  Topografia + Paquimetria na mesma visita). Opcional — quando
   *  ausente/vazio, a cobrança é o `chargedAmount` único (modo simples,
   *  retrocompatível). Quando presente, `chargedAmount` = Σ price. */
  procedures?: AppointmentProcedure[];
  /** Quando a cobrança foi registrada no sistema (ms epoch). */
  chargedAt?: number;
  /** UID do usuário que registrou a cobrança — auditoria. */
  chargedBy?: string;
  /** Nome do usuário que cobrou (snapshot — denormalizado pra
   *  permanecer legível mesmo se o usuário for desativado depois). */
  chargedByName?: string;
  /** Forma de pagamento — rótulo legível, mantido em sincronia como
   *  resumo de `paymentParts` (ex.: "Pix", "Crédito 3×", "Crédito 6× +
   *  Pix"). Em agendamentos antigos pode ser texto livre. Preferir
   *  `paymentParts` como fonte de verdade quando presente. */
  paymentMethod?: string;
  /** Composição estruturada do pagamento — uma forma (forma única) ou
   *  várias (pagamento híbrido). Cartão de crédito carrega o nº de
   *  parcelas. Fonte de verdade da forma de pagamento pro relatório de
   *  recebimentos. Ausente/vazio em agendamentos antigos (cai no
   *  `paymentMethod` texto livre). */
  paymentParts?: PaymentPart[];
  /** Status da cobrança — independente do `status` do agendamento.
   *   - PENDENTE: cobrança em aberto, paciente ainda não pagou
   *   - PAGO: pagamento confirmado
   *   - ISENTO: cortesia / não há cobrança
   *   - CONVENIO: o plano paga depois (faturamento ao convênio) — nada é
   *     cobrado do paciente; `chargedAmount` fica 0 como no ISENTO
   *   - RETORNO: retorno dentro do prazo da consulta — sem cobrança, como o ISENTO
   *   - PARCIAL: pago parte (sinal, cartão parcelado, etc.) */
  paymentStatus?: 'PENDENTE' | 'PAGO' | 'ISENTO' | 'CONVENIO' | 'PARCIAL' | 'RETORNO';
  /** QUANDO o dinheiro entrou (independente da forma — Pix, espécie…):
   *   - ANTECIPADO: pago antes do dia (registrado em fichário/caderno);
   *   - HOJE: pago no balcão, no dia.
   *  Ausente = pendente, sem cobrança ou registro antigo. */
  paymentTiming?: 'ANTECIPADO' | 'HOJE';
  /** Onde o pagamento antecipado está registrado (ex.: "fichário 3, pág.
   *  12"). Só faz sentido com `paymentTiming='ANTECIPADO'`. */
  paymentNote?: string;
  /** Valor já recebido do paciente — sinal/entrada de cirurgia, parcela
   *  adiantada. Relevante quando `paymentStatus='PARCIAL'`: o saldo a
   *  receber é `chargedAmount - amountPaid`. Para PAGO o recebido é o
   *  total (implícito); para PENDENTE/ISENTO é zero. Procedimentos de
   *  valor alto (capsulotomia, faco) costumam ter sinal antecipado. */
  amountPaid?: number;
  /** Histórico de adiantamentos (sinal + parcelas), em ordem cronológica.
   *  `amountPaid` é a soma destes. Opcional — quando ausente, o staff
   *  apenas digitou o total recebido sem detalhar cada entrada. */
  payments?: AppointmentPayment[];

  // ── Pendência financeira (contas a receber) ──────────────────────
  // A pendência NÃO é um doc separado: é derivada da cobrança acima
  // (ver `src/utils/receivables.ts`). Devedor = paciente quando
  // PENDENTE/PARCIAL; = convênio (`insurerId`) quando CONVENIO. Estes
  // campos só guardam o combinado e a baixa.

  /** Data prevista pro pagamento (ms epoch, meia-noite local). */
  paymentDueDate?: number;
  /** Forma combinada pro pagamento que ainda vai entrar. */
  paymentAgreedMethod?: PaymentPart['method'];
  /** Valor a faturar ao convênio. A cobrança CONVENIO grava
   *  `chargedAmount = 0` (nada sai do paciente); o que o plano deve vive
   *  aqui. Ausente = cai no `expectedPrice` (tabela na época). */
  insurerAmount?: number;
  /** Observação livre da pendência (nº da guia, lote, combinado…). */
  receivableNote?: string;
  /** Baixa: quando o valor foi efetivamente recebido. Pro convênio é o
   *  que fecha a pendência; pro paciente a baixa também vira PAGO. */
  receivableSettledAt?: number;
  receivableSettledBy?: string;
  receivableSettledByName?: string;
  /** Valor recebido na baixa (convênio pode pagar menos — glosa). */
  receivableSettledAmount?: number;
  receivableSettledMethod?: PaymentPart['method'];

  // ── Soft delete (Desmarcar — remove da agenda mas preserva auditoria) ──
  /** Quando true, o agendamento foi "desmarcado" — sai da view da agenda
   *  mas o registro continua no Firestore pra auditoria. Diferente de
   *  CANCELADA (que apenas marca status, mantendo visível). */
  isDeleted?: boolean;
  /** Timestamp (ms) do soft-delete. */
  deletedAt?: number;
  /** UID de quem desmarcou. */
  deletedBy?: string;

  // ── Cronômetro do atendimento ─────────────────────────────────────
  /** Epoch ms — quando o paciente chegou (check-in da recepção ou
   *  mudança manual pra EM_ESPERA). Espelha o `arrivedAt` da entrada
   *  na fila, mas fica no próprio agendamento pra minutagem de espera
   *  (chegada → início do atendimento) funcionar em qualquer dia sem
   *  depender da collection `queue`. */
  checkedInAt?: number;
  /** Epoch ms — quando o staff iniciou o atendimento (clicou em
   *  "Iniciar atendimento" na ficha do paciente ou mudou status pra
   *  EM_ATENDIMENTO no detail modal). */
  attendanceStartedAt?: number;
  /** Epoch ms — quando o atendimento foi concluído. */
  attendanceEndedAt?: number;
  /** Duração total do atendimento em segundos (calculado quando
   *  finalizado). Snapshot pra evitar recálculo / drift de timezone.
   *  Também usado como acumulador quando o atendimento é PAUSADO pra
   *  dilatação (ver `attendancePausedForDilation`). */
  attendanceDurationSec?: number;
  /** Oftalmo: quando true, o atendimento está PAUSADO porque o paciente
   *  foi enviado pra dilatar a pupila (status volta pra EM_ESPERA, mas
   *  o cronômetro fica congelado em `attendanceDurationSec`). A ficha NÃO
   *  auto-reinicia o atendimento nesse estado — o médico retoma manualmente
   *  ("Retomar atendimento"), continuando a contagem de onde parou. */
  attendancePausedForDilation?: boolean;
  /** Oftalmo: epoch ms de quando o paciente foi enviado pra dilatar
   *  (carimbado junto com `attendancePausedForDilation`). É comparado com
   *  o `attendanceStartedAt` dos demais agendamentos pra decidir o
   *  "próximo": o paciente em dilatação só vira o próximo DEPOIS que o
   *  médico inicia o atendimento de outro paciente (a pupila ainda está
   *  dilatando até lá). Ver `pickNextUp` em utils/nextUp. Limpo no
   *  `resumeFromPause`. */
  dilationPausedAt?: number;
  /** Quando true, o atendimento está PAUSADO porque o médico FECHOU a ficha
   *  do paciente (status volta pra EM_ESPERA, cronômetro congela em
   *  `attendanceDurationSec`). Diferente da dilatação: reabrir a ficha
   *  RETOMA sozinho (auto-resume), sem precisar de clique manual — o
   *  paciente segue fisicamente na sala, só a ficha estava fechada. Limpo
   *  no `resumeFromPause`. Ver `AttendanceTimerCard`. */
  attendancePausedForClose?: boolean;

  // ── "Próximo a ser atendido" ──────────────────────────────────────
  /** Quando true, este agendamento foi marcado manualmente pela
   *  secretária como o próximo paciente a entrar. Vale por dia: ao
   *  marcar um, os demais do mesmo dia são desmarcados em batch.
   *  Limpo automaticamente quando o status vai pra EM_ATENDIMENTO,
   *  CONCLUIDA, CANCELADA, FALTOU ou em soft-delete. */
  isNextUp?: boolean;
  /** Epoch ms da última vez que foi marcado como próximo. */
  nextUpMarkedAt?: number;
  /** UID de quem marcou. */
  nextUpMarkedBy?: string;

  // ── Briefing pós-atendimento pra secretária ───────────────────────
  /**
   * Snapshot do que foi solicitado durante a consulta — calculado
   * automaticamente quando o atendimento vai pra CONCLUIDA. Quando
   * presente e `briefed=false`, o agendamento entra na "Caixa de
   * Pendências" da secretária pra que ela oriente o paciente sobre
   * exames, procedimentos/cirurgias e retornos pedidos.
   *
   * Ausente significa "nada a orientar" (consulta sem pedidos) —
   * intencionalmente não persistimos um briefing vazio pra evitar
   * ruído no painel.
   */
  secretaryBriefing?: {
    /** Houve EXAM_REQUEST com 1+ exames listados na janela do atendimento. */
    hasExams: boolean;
    /** Houve SURGERY na janela do atendimento. */
    hasProcedures: boolean;
    /** Houve EXAM_REQUEST sem exames (Solicitação de Retorno) na janela. */
    hasReturn: boolean;
    /** IDs das prescriptions que originaram o briefing — pra rastrear. */
    prescriptionIds: string[];
    /** Epoch ms quando o briefing foi gerado. */
    createdAt: number;
    /** True quando a secretária marcou "paciente orientado". */
    briefed: boolean;
    /** Epoch ms da confirmação. */
    briefedAt?: number;
    /** UID de quem marcou como orientado. */
    briefedBy?: string;
    /** Nome (snapshot) de quem orientou — preserva legibilidade mesmo
     *  se o usuário for desativado depois. */
    briefedByName?: string;
  };
}

export interface ClinicalRecord {
  id: string;
  patientId: string;
  clinicId: string;
  professionalId: string;
  appointmentId?: string;
  type: 'CONSULTATION' | 'EXAM' | 'PROCEDURE' | 'TELECONSULTATION';
  content: Record<string, unknown>;
  /**
   * Categoria de topo indexável. 'IMAGE' marca registros que são apenas
   * imagens anexadas — usado para permitir que a secretária (permissão
   * "Imagens da ficha") crie/veja essas entradas sem ler o resto do
   * prontuário. Ver firestore.rules → records.
   */
  category?: 'IMAGE';
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
    /** Caminho no Firebase Storage — preservado pra permitir delete posterior. */
    storagePath?: string;
    /** Tamanho em bytes do arquivo enviado (após compressão). */
    size?: number;
  }>;
  /**
   * Rótulo de exibição personalizado pelo médico (coluna de registros do
   * prontuário). Vazio/ausente → usa o rótulo automático do tipo
   * (Evolução Clínica, Receita de Lentes, etc.). É só um rótulo visual:
   * não altera o `type` nem os filtros por tipo da coluna.
   */
  displayTitle?: string;
  version: number;
  source?: 'HUMAN' | 'AI_ASSISTED' | 'INTEGRATION';
  aiGenerated?: boolean;
  aiModel?: string;
  updatedBy: string;
  createdAt: number;
  updatedAt: number;
  /**
   * Data ORIGINAL do atendimento, em epoch ms. Preenchida só nos registros
   * migrados do HiDoctor (em que `createdAt` é o instante da migração, não do
   * atendimento). Use `recordEventDate` (utils/recordDate) para ler a data a
   * exibir/ordenar — ela cai no `createdAt` quando este campo não existe.
   */
  recordDate?: number;
}

export interface AgendaBreak {
  /** "HH:MM" */
  start: string;
  /** "HH:MM" */
  end: string;
  /** Rótulo opcional (ex.: "Almoço") — aparece na faixa da grade. */
  label?: string;
}

export interface AgendaConfigDay {
  enabled: boolean;
  start: string;
  end: string;
  /** Intervalos sem atendimento dentro do expediente (almoço etc.).
   *  A grade desenha a faixa, recusa criar/arrastar pra dentro e as
   *  vagas livres pulam o período. */
  breaks?: AgendaBreak[];
}

export interface AgendaConfig {
  clinicId: string;
  defaultDuration: number; // minutes
  overbookingLimit: number;
  /**
   * Grade de horários FIXOS: quando ligada, o "Início" do Novo Agendamento
   * vira uma lista fechada de horários de `slotIntervalMin` em
   * `slotIntervalMin` minutos (00:00 → 23:xx) — a secretária não consegue
   * digitar horário fora da grade. Também habilita a opção "sem hora"
   * (paciente na sequência, prefixo "A seguir").
   */
  fixedSlots?: boolean;
  /** Intervalo da grade fixa em minutos (ex.: 20 → 00:00, 00:20 … 23:40). */
  slotIntervalMin?: number;
  /** Portal: consulta longa (exame, procedimento) bloqueia todos os horários
   *  que ocupa, não só o de início. Default true. Não afeta a agenda interna. */
  portalBlocksFullDuration?: boolean;
  shiftBoundary?: string; // e.g. "12:00" — divides morning/afternoon shifts (default "12:00")
  /** Quantos médicos atendem em consultório ao mesmo tempo. Ausente = sem
   *  limite. Cirurgia (bloco) não conta — ver `utils/consultorios.ts`. */
  consultorios?: number;
  days: {
    0: AgendaConfigDay; // Sunday
    1: AgendaConfigDay; // Monday
    2: AgendaConfigDay; // Tuesday
    3: AgendaConfigDay; // Wednesday
    4: AgendaConfigDay; // Thursday
    5: AgendaConfigDay; // Friday
    6: AgendaConfigDay; // Saturday
  };
  updatedAt: number;
}

// ── Smart Slots (Patient Self-Scheduling) ──

export type ShiftType = 'MANHA' | 'TARDE';

export interface SmartSlotRequest {
  clinicId: string;
  specialtyId: string;
  unitId: string;
  preferredDate: string;     // 'YYYY-MM-DD'
  shift: ShiftType;
  flexible: boolean;
}

export interface SmartSlotDoctorGroup {
  doctorId: string;
  doctorName: string;
  specialty: string;
  availableSlots: string[];  // HH:MM
}

export interface SmartSlotDateGroup {
  date: string;              // 'YYYY-MM-DD'
  displayDate: string;       // 'Qua, 15/04'
  isPreferred: boolean;
  doctors: SmartSlotDoctorGroup[];
}

export interface SmartSlotResponse {
  preferredDateSlots: SmartSlotDateGroup | null;
  nearbyDateSlots: SmartSlotDateGroup[];
  noSlotsOnPreferred: boolean;
}

// Note: ClinicalRecord interface is defined above with full fields

/**
 * Status de execução de procedimentos solicitados (SURGERY / EXAM_REQUEST).
 * `PENDING` é o default ao criar; vira `DONE` quando a secretária/médico
 * confirma que foi realizado, ou `CANCELED` se a solicitação foi abandonada.
 * Demais tipos de Prescription (LENS, MEDICATION, etc.) não usam este campo.
 */
export type PrescriptionExecutionStatus = 'PENDING' | 'DONE' | 'CANCELED';

export interface Prescription {
  id: string;
  clinicId: string;
  patientId: string;
  professionalId: string;
  type: 'LENS' | 'MEDICATION' | 'EXAM_REQUEST' | 'ATTESTATION' | 'RECEIPT' | 'PRESCRIPTION' | 'CERTIFICATE' | 'SURGERY' | 'REFERRAL';
  content: any;
  /**
   * Rótulo de exibição personalizado pelo médico (coluna de registros do
   * prontuário). Vazio/ausente → usa o rótulo automático do tipo. É só um
   * rótulo visual: não altera o `type` nem os filtros por tipo da coluna.
   */
  displayTitle?: string;
  signatureStatus: 'UNSIGNED' | 'SIGNED_ICP' | 'SIGNED_MANUAL';
  createdAt: number;
  /**
   * Painel de Pendências (Procedimentos em atraso): só aplicável a
   * SURGERY e EXAM_REQUEST. Ausência é tratada como `PENDING` para
   * documentos criados antes do release da feature.
   */
  executionStatus?: PrescriptionExecutionStatus;
  /** Quando foi marcado como realizado (epoch ms). */
  executedAt?: number;
  /** UID do profissional/secretária que marcou como realizado. */
  executedBy?: string;
  /** Observação livre sobre a execução (ex.: "Realizado em 03/05/2026 - Hosp X"). */
  executionNote?: string;
}

export interface Budget { id: string; }
export interface Income { id: string; }
export interface Expense { id: string; }
export interface AIAnalysis { id: string; }
export interface Template {
  id: string;
  clinicId: string;
  type: 'PRESCRIPTION' | 'EXAM' | 'CERTIFICATE' | 'SURGERY' | 'REFERRAL' | 'LENS_ORDER_EMAIL';
  title: string;
  content: string;
  isActive: boolean;
  createdAt: number;
  /** Email-order-only campos; ver ClinicalTemplate em services/firebase/templates.ts. */
  emailSubject?: string;
  emailTo?: string;
  emailCc?: string;
}
export interface Insurer { id: string; }

/**
 * Entrada no histórico de alterações de um agendamento (Fase 4 — agenda).
 *
 * Persistido em `clinics/{cid}/appointments/{aid}/history/{id}`. Gravado
 * por `updateAppointmentTracked` antes da atualização — captura o diff
 * dos campos rastreados (status, horário, profissional, tipo, convênio)
 * com snapshot do `from` e `to`. Cobrança tem audit dedicado nos próprios
 * campos do Appointment (`chargedAt`/`chargedBy`), então não duplica aqui.
 */
export interface AppointmentHistoryEntry {
  id: string;
  clinicId: string;
  appointmentId: string;
  /** Map de field → { from, to }. Tipado como unknown porque
   *  rastreamos status, strings de horário e ids — depende do field. */
  changes: Record<string, { from?: unknown; to?: unknown }>;
  /** Ação semântica que originou a mudança (opcional, melhora a leitura
   *  do timeline: "drag-drop", "cancel", "status-change"). */
  action?: string;
  actorUid: string;
  actorName?: string;
  at: number;
}

/**
 * Recado interno entre staff por paciente (Fase 4 — agenda).
 *
 * Persistido em `clinics/{cid}/patients/{pid}/internalNotes/{id}`.
 * Distinto do campo `notes` do Appointment (que é compartilhado entre
 * staff e paciente em alguns canais) — aqui é estritamente interno,
 * pra comunicação entre recepção e médico ("paciente disse que está em
 * jejum", "lembrar de pedir RG na chegada", etc.).
 *
 * `audience` indica pra quem o recado é destinado (recepção, médico ou
 * todos). O UI usa pra filtrar e priorizar visual.
 */
export interface InternalNote {
  id: string;
  clinicId: string;
  patientId: string;
  text: string;
  audience: 'ALL' | 'RECEPTION' | 'DOCTOR';
  createdAt: number;
  createdBy: string;
  createdByName?: string;
  /** Quando o recado foi marcado como lido/resolvido. Não-deletado pra
   *  preservar histórico, mas some das listas "ativas" por padrão. */
  resolvedAt?: number;
  resolvedBy?: string;
  resolvedByName?: string;
}

/**
 * Entrada da lista de espera de uma clínica (Fase 3 — agenda).
 *
 * Persistido em `clinics/{cid}/waitlist/{id}`. Representa um paciente
 * que quer agendar mas não há horário conveniente — ou está esperando
 * um cancelamento. Quando o staff agenda esse paciente, o item da
 * lista é marcado como SCHEDULED (não deletado) pra preservar histórico
 * e métricas de "quanto tempo o paciente esperou".
 */
export interface WaitlistEntry {
  id: string;
  clinicId: string;
  patientId: string;
  /** Nome do paciente — denormalizado pra evitar query extra na lista. */
  patientName?: string;
  /** Telefone do paciente — denormalizado pra contato rápido. */
  patientPhone?: string;
  professionalId?: string;
  preferredType?: 'CONSULTA' | 'RETORNO' | 'PRIMEIRA_VEZ' | 'EXAME' | 'TELEMEDICINA' | 'PROCEDIMENTO';
  /** Preferência de turno. ANY = qualquer hora. */
  preferredTime?: 'MORNING' | 'AFTERNOON' | 'ANY';
  /** Dias da semana preferidos (0=domingo, 6=sábado). Vazio = qualquer. */
  preferredDays?: number[];
  /**
   * Janelas de datas desejadas — "quero o dia 29/08", "a semana de 26/07 a
   * 01/08", "setembro OU outubro". Lista vazia/ausente = qualquer data. As
   * pontas são ISO `YYYY-MM-DD` fechadas nos dois lados, pra o filtro
   * comparar por sobreposição — ver `src/utils/waitlistPeriod.ts`.
   */
  periods?: import('@/utils/waitlistPeriod').WaitlistPeriod[];
  /**
   * LEGADO — janela única, da primeira versão do recurso. Só é lida quando
   * `periods` não existe; nada novo escreve aqui.
   */
  periodKind?: 'ANY' | 'DAY' | 'WEEK' | 'MONTH';
  periodStart?: string;
  periodEnd?: string;
  /** Observações livres ("urgência", "alérgico a colírio X", etc.). */
  notes?: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH';
  status: 'WAITING' | 'CONTACTED' | 'SCHEDULED' | 'REMOVED';
  /** Quando o staff entrou em contato (WhatsApp/ligação). Atualiza ao
   *  clicar em "Marcar como contactado". */
  contactedAt?: number;
  /** Quando o paciente foi efetivamente agendado, e o id do appointment
   *  resultante (pra navegação 1-clique). */
  scheduledAppointmentId?: string;
  scheduledAt?: number;
  removedAt?: number;
  removedReason?: string;
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
}

/**
 * Tipo de compromisso customizável da clínica (Fase 2 — agenda).
 *
 * Persistido em `clinics/{cid}/appointmentTypes/{id}`. Coexiste com o
 * enum legado `Appointment['type']` — quando o agendamento referencia
 * um custom via `appointmentTypeId`, a UI prioriza nome e cor daqui;
 * caso contrário, cai no rótulo enum. Permite à clínica cadastrar
 * "RETINOGRAFIA", "OCT", "CAPSULOTOMIA", "CRISTAL PREMIUM" etc. sem
 * mexer no schema do `Appointment` (que continua exigindo `type`).
 *
 * `color` é livre — hex ou token CSS. Default herda do status.
 */
export interface AppointmentType {
  id: string;
  clinicId: string;
  name: string;
  color?: string;
  defaultDurationMin?: number;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number;
  createdBy?: string;
}

/**
 * Tabela de preços por procedimento/exame/consulta da clínica.
 * Stored em `clinics/{clinicId}/procedurePrices/{id}`.
 *
 * Chave de busca em runtime (`Appointment.type` + opcionalmente
 * `examType` + `insurerId`):
 *   - `type='EXAME' AND examType='OCT' AND insurerId='bradesco'`
 *   - cai pra `insurerId=null` (particular) se não achar específico
 *
 * Ativar/desativar é o caminho recomendado (`active=false`); deletar
 * só quando o registro foi criado por engano. Snapshot do histórico
 * (`expectedPrice` no Appointment) protege agendamentos antigos.
 */
export interface ProcedurePrice {
  id: string;
  clinicId: string;
  type: 'CONSULTA' | 'RETORNO' | 'PRIMEIRA_VEZ' | 'EXAME' | 'TELEMEDICINA' | 'PROCEDIMENTO';
  /** Quando `type='EXAME'`, especifica qual exame (OCT, paquimetria, etc.).
   *  Texto livre — não há enumeração formal. */
  examType?: string;
  /** Convênio aplicável; `null`/ausente = particular. */
  insurerId?: string;
  /** Nome humano do convênio (denormalizado pra evitar join na lista). */
  insurerName?: string;
  /** Preço em reais (decimal). */
  price: number;
  /** Observação opcional (ex.: "inclui retorno", "pacote de 3 sessões"). */
  notes?: string;
  active: boolean;
  createdAt: number;
  updatedAt: number;
  updatedBy: string;
}
export interface InventoryItem { id: string; }
export interface AuditLog { id: string; }
export interface QueueEntry {
  id: string;
  clinicId: string;
  unitId: string;
  patientId: string;
  /** Nome do paciente denormalizado. Necessário pra página pública
   *  /fila/:cid/:id (acessada via QR no celular do paciente) sem ter
   *  que ler a coleção patients — evita expor PII por rules abertas.
   *  Populado por quem cria a entrada (totem, recepção). */
  patientName?: string;
  appointmentId?: string;
  professionalId: string;
  /** Nome do médico denormalizado pela mesma razão de `patientName`. */
  professionalName?: string;
  /** Local de atendimento onde o paciente chegou (ver utils/serviceLocal) —
   *  só a recepção do mesmo local recebe chamada/dilatação dele. */
  serviceLocal?: string;
  status: 'WAITING' | 'CALLED' | 'IN_SERVICE' | 'COMPLETED' | 'NO_SHOW';
  ticket: string;
  ticketNumber?: string;
  room?: string;
  roomString?: string;
  calledAt?: number;
  /** UID de quem disparou a chamada — usado para NÃO mostrar o pop-up de
   *  "Chamar paciente" para o próprio autor da chamada. */
  calledBy?: string;
  // ── Dilatação de pupila (oftalmo) ─────────────────────────────────
  /** Epoch ms — quando o médico enviou o paciente de volta pra recepção
   *  pra DILATAR a pupila. Carimbar dispara o alerta da recepção
   *  (CallAlertWatcher) e o selo "Dilatar" na fila; o status volta pra
   *  WAITING (sala de espera). Re-pedir (novo timestamp) re-alerta. */
  dilationRequestedAt?: number;
  /** UID de quem pediu a dilatação — não mostra o alerta a quem pediu
   *  (mesmo padrão de `calledBy`). */
  dilationRequestedBy?: string;
  /** Sala do médico (snapshot) — a recepção sabe pra onde devolver o
   *  paciente depois de dilatado. */
  dilationRoom?: string;
  arrivedAt: number;
  waitTimeMinutes: number;
  createdAt: number;
}

/**
 * Token público que identifica um painel de chamada (TV na sala de espera).
 * O doc.id é o próprio token — usado na URL `/painel/{token}` e validado
 * pela Cloud Function `proxyQueue` antes de devolver dados sanitizados da
 * fila. Sem token = sem acesso; tokens revogados (`active=false`) também
 * são recusados pela CF.
 *
 * Top-level (`panelTokens/{token}`) — facilita lookup direto pela CF sem
 * precisar conhecer o clinicId.
 */
export interface PanelToken {
  /** O próprio token aleatório (também é o doc.id). */
  token: string;
  clinicId: string;
  /** Rótulo descritivo escolhido pelo admin (ex.: "Sala de espera térreo"). */
  label: string;
  /** Quando preenchido, o painel filtra a fila apenas por esta unidade. */
  unitId?: string;
  active: boolean;
  createdAt: number;
  /** UID do usuário que gerou o token (para auditoria). */
  createdBy: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mural de Recados
// ─────────────────────────────────────────────────────────────────────────────

/** Colunas do mural. A ordem aqui é a ordem visual do quadro. */
export const MURAL_STATUSES = ['anotado', 'aguardando', 'resolvido'] as const;
export type MuralStatus = (typeof MURAL_STATUSES)[number];

/** Categoria do recado — define a cor do post-it. */
export const MURAL_CATEGORIES = ['receita', 'exame', 'agenda', 'convenio', 'outro'] as const;
export type MuralCategory = (typeof MURAL_CATEGORIES)[number];

/**
 * Recado do balcão. Persistido em `clinics/{clinicId}/muralNotes/{id}`.
 *
 * É o inverso da Caixa de Pendências (`usePendencias`): lá o sistema DEDUZ
 * o que está pendente a partir de agendamentos/prescrições/estoque; aqui uma
 * PESSOA digitou o que o paciente pediu no balcão. As duas telas não se
 * misturam de propósito.
 *
 * `patientId` é opcional — "a representante da Alcon passa quinta" é recado
 * válido. Sem paciente, o post-it não aparece em busca por nome/CPF nem no
 * histórico de ficha nenhuma, e a UI marca isso explicitamente.
 */
export interface MuralNote {
  id: string;
  clinicId: string;
  status: MuralStatus;
  category: MuralCategory;
  /** O recado como o paciente falou. Texto livre. */
  text: string;

  /** Paciente vinculado (opcional — ver doc da interface). */
  patientId?: string;
  patientName?: string;
  /**
   * CPF em claro (11 dígitos) ou `P-######` quando o paciente não tem CPF.
   * Espelha `Patient.patientNumber` — o campo `Patient.cpf` vem cifrado do
   * Firestore e é inútil pra exibir/buscar (ver `utils/patientCpf.ts`).
   */
  patientNumber?: string;

  /** ms epoch. Define a idade do post-it (o selo "3 dias"). */
  createdAt: number;
  /** Dia de Brasília (YYYY-MM-DD) em que o recado nasceu. */
  dayKey: string;
  createdBy: string;
  createdByName?: string;

  /** Última movimentação de coluna — usado só pra ordenar Aguardando. */
  updatedAt?: number;

  /** Preenchidos ao resolver; zerados no Desfazer / ao voltar pra coluna. */
  resolvedAt?: number;
  resolvedBy?: string;
  resolvedByName?: string;
  /**
   * Dia de Brasília em que foi resolvido. É ISSO que faz o post-it sumir do
   * mural na virada: a tela só assina os resolvidos de HOJE. Sem cron.
   */
  resolvedDayKey?: string;

  /**
   * Acréscimos ao recado — a "conversa" de quem está resolvendo. Só cresce:
   * ninguém edita nem apaga o que outro escreveu; cada linha carrega o autor
   * (nome + cargo) carimbado no momento em que foi escrita.
   */
  comments?: MuralComment[];

  /**
   * Origem quando o post-it nasceu de uma mensagem do chat interno. Snapshot
   * de quem mandou e quando — a mensagem pode ser apagada depois, o recado não.
   */
  fromChat?: MuralChatSource;

  /**
   * Pede pop-up na tela da recepção ([[MuralAlertWatcher]]). Marcado quando o
   * MÉDICO cola uma mensagem do chat no mural — post-it colado pela própria
   * recepção não avisa ninguém (é ela quem cuida do mural).
   */
  receptionAlert?: boolean;
  /**
   * "Ciente" da recepção: o primeiro que confirma tira o pop-up de todas as
   * telas. Fica no post-it pra o médico ver que a recepção leu.
   */
  receptionSeenAt?: number;
  receptionSeenBy?: string;
  receptionSeenByName?: string;
}

export interface MuralChatSource {
  channelId: string;
  messageId: string;
  channelName?: string;
  senderName?: string;
  /** ms epoch da mensagem original. */
  sentAt?: number;
}

export interface MuralComment {
  id: string;
  text: string;
  /** ms epoch. */
  at: number;
  by: string;
  byName?: string;
  /** Cargo por extenso no momento em que escreveu ("Secretária", "Médico"…). */
  byRole?: string;
}
